import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ContactModal from '../ContactModal';

const ChevronDown = ({ open }) => (
  <svg
    className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const navItems = [
  {
    label: 'Quiénes somos',
    children: [
      { href: '/#sobre-inerva', label: 'Qué es Inerva' },
      { href: '/#equipo', label: 'Nuestro equipo' },
      { href: '/#valores', label: 'Valores' },
    ],
  },
  {
    label: 'Servicios',
    children: [
      { href: '/servicios#servicios', label: 'Nuestros servicios' },
      { href: '/servicios#metodologia', label: 'Metodología' },
      { href: '/servicios#destinatarios', label: 'A quién va dirigido' },
    ],
  },
  { href: '/tarifas', label: 'Tarifas' },
  { href: '/faq', label: 'FAQ' },
  { href: '/blog', label: 'Blog' },
];

function scrollToHash(hash) {
  if (!hash) return;
  const id = hash.replace('#', '');
  const el = document.getElementById(id);
  if (el) {
    setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  }
}

export default function Header() {
  const [showModal, setShowModal] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDesktop, setOpenDesktop] = useState(null);
  const [openMobile, setOpenMobile] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const closeTimer = useRef(null);
  const dropdownRefs = useRef({});

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (location.hash) scrollToHash(location.hash);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleClick = (e) => {
      if (openDesktop !== null) {
        const ref = dropdownRefs.current[openDesktop];
        if (ref && !ref.contains(e.target)) setOpenDesktop(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openDesktop]);

  const handleNavClick = (href) => {
    setIsMobileMenuOpen(false);
    setOpenDesktop(null);
    setOpenMobile(null);

    const [path, hash] = href.split('#');
    const currentPath = location.pathname;

    if (path === currentPath || (path === '/' && currentPath === '/')) {
      if (hash) scrollToHash('#' + hash);
    } else {
      navigate(path + (hash ? '#' + hash : ''));
    }
  };

  const linkClass = 'text-[var(--color-inerva-forest)] font-medium text-sm tracking-wide px-4 py-2 rounded-full hover:bg-mint transition-all no-underline';
  const mobileLinkClass = 'text-[var(--color-inerva-forest)] font-medium text-lg py-3 px-4 rounded-xl hover:bg-mint transition-colors no-underline block';
  const mobileSubLinkClass = 'text-[var(--color-inerva-forest)]/80 text-base py-2.5 px-4 pl-8 rounded-xl hover:bg-mint transition-colors no-underline block';

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-4'
        }`}
      >
        <div className="container-narrow">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src="/assets/Logo.png"
                alt="Inerva Psicología"
                className={`w-auto object-contain transition-all duration-300 group-hover:scale-105 ${
                  isScrolled ? 'h-[4rem] md:h-14' : 'h-[7rem] md:h-20 lg:h-24'
                }`}
              />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-6">
              {navItems.map((item, idx) =>
                item.children ? (
                  <div
                    key={item.label}
                    className="relative"
                    ref={(el) => (dropdownRefs.current[idx] = el)}
                    onMouseEnter={() => {
                      clearTimeout(closeTimer.current);
                      setOpenDesktop(idx);
                    }}
                    onMouseLeave={() => {
                      closeTimer.current = setTimeout(() => setOpenDesktop(null), 150);
                    }}
                  >
                    <button
                      className={`${linkClass} flex items-center gap-1.5 cursor-pointer bg-transparent border-none`}
                      onClick={() => setOpenDesktop(openDesktop === idx ? null : idx)}
                    >
                      {item.label}
                      <ChevronDown open={openDesktop === idx} />
                    </button>
                    <div
                      className={`absolute top-full left-0 mt-1 min-w-[200px] bg-white rounded-xl shadow-lg border border-[var(--color-inerva-forest)]/5 py-2 transition-all duration-200 origin-top ${
                        openDesktop === idx
                          ? 'opacity-100 scale-100 pointer-events-auto'
                          : 'opacity-0 scale-95 pointer-events-none'
                      }`}
                    >
                      {item.children.map((child) => (
                        <button
                          key={child.href}
                          onClick={() => handleNavClick(child.href)}
                          className="w-full text-left px-4 py-2.5 text-sm text-[var(--color-inerva-forest)] hover:bg-mint transition-colors no-underline bg-transparent border-none cursor-pointer font-medium"
                        >
                          {child.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link key={item.href} to={item.href} className={linkClass}>
                    {item.label}
                  </Link>
                )
              )}
              <button onClick={() => setShowModal(true)} className="btn-primary text-sm py-3 px-6">
                Contactar
              </button>
            </nav>

            {/* Mobile hamburger */}
            <button
              className="md:hidden relative w-11 h-11 flex items-center justify-center group border-none outline-none bg-transparent appearance-none"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            >
              <div className="relative w-6 h-5 flex flex-col justify-center items-center">
                <span className={`absolute h-[2px] rounded-full bg-[var(--color-inerva-forest)] transition-all duration-300 ease-out ${
                  isMobileMenuOpen ? 'w-6 rotate-45' : 'w-6 -translate-y-2 group-hover:w-5'
                }`} />
                <span className={`absolute h-[2px] w-4 rounded-full bg-[var(--color-inerva-forest)] transition-all duration-300 ease-out ${
                  isMobileMenuOpen ? 'opacity-0 scale-0' : 'opacity-100 group-hover:w-6'
                }`} />
                <span className={`absolute h-[2px] rounded-full bg-[var(--color-inerva-forest)] transition-all duration-300 ease-out ${
                  isMobileMenuOpen ? 'w-6 -rotate-45' : 'w-5 translate-y-2 group-hover:w-4'
                }`} />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile overlay */}
      <div
        className={`md:hidden fixed inset-0 z-40 bg-[var(--color-inerva-forest)]/40 backdrop-blur-sm transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile drawer */}
      <div
        className={`md:hidden fixed top-0 right-0 bottom-0 z-50 w-[80%] max-w-[300px] bg-[var(--color-inerva-cream)] shadow-[-8px_0_30px_rgba(0,0,0,0.1)] transition-transform duration-300 ease-out rounded-l-2xl ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-end p-5">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[var(--color-inerva-forest)]/5 transition-colors"
              aria-label="Cerrar menú"
            >
              <svg className="w-5 h-5 text-[var(--color-inerva-forest)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <nav className="flex-1 py-6 px-5 overflow-y-auto">
            <div className="flex flex-col gap-1">
              {navItems.map((item, idx) =>
                item.children ? (
                  <div key={item.label}>
                    <button
                      onClick={() => setOpenMobile(openMobile === idx ? null : idx)}
                      className={`${mobileLinkClass} flex items-center justify-between w-full bg-transparent border-none cursor-pointer`}
                    >
                      {item.label}
                      <ChevronDown open={openMobile === idx} />
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-200 ${
                        openMobile === idx ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      {item.children.map((child) => (
                        <button
                          key={child.href}
                          onClick={() => handleNavClick(child.href)}
                          className={`${mobileSubLinkClass} w-full text-left bg-transparent border-none cursor-pointer`}
                        >
                          {child.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={mobileLinkClass}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                )
              )}
            </div>
          </nav>

          <div className="p-5 border-t border-[var(--color-inerva-forest)]/10">
            <button
              onClick={() => { setShowModal(true); setIsMobileMenuOpen(false); }}
              className="btn-primary w-full text-base py-4"
            >
              Contactar
            </button>
          </div>
        </div>
      </div>

      <ContactModal show={showModal} handleClose={() => setShowModal(false)} />
    </>
  );
}
